/**
 * Kleiner Helfer für Daten vom Server: lädt, meldet Fehler und erlaubt
 * ein erneutes Laden. Bewusst ohne zusätzliche Bibliothek, damit die
 * Anwendung schlank bleibt.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '../api/client.ts';

/** Ergebnis eines Ladevorgangs. */
export interface QueryResult<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
  reload: () => void;
  setData: (data: T | null) => void;
}

/**
 * Lädt Daten von einem Pfad.
 * @param path Pfad der Schnittstelle; bei null wird nicht geladen.
 */
export function useQuery<T>(path: string | null, deps: readonly unknown[] = []): QueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [error, setError] = useState<string | null>(null);
  const [counter, setCounter] = useState(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (path === null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get<T>(path)
      .then((result) => {
        if (alive.current) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((caught: unknown) => {
        if (alive.current) {
          setError(caught instanceof ApiError ? caught.message : 'Unbekannter Fehler.');
          setLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, counter, ...deps]);

  const reload = useCallback(() => setCounter((value) => value + 1), []);

  return { data, loading, error, reload, setData };
}
